import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSessions } from '../services/api';
import Flag from './ui/Flag';
import PageLayout from './ui/PageLayout';

function MatchArchivePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search,         setSearch]         = useState(searchParams.get('athlete') || '');
  const [filterEvent,    setFilterEvent]    = useState('');
  const [filterWeight,   setFilterWeight]   = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');
  const [sortBy,         setSortBy]         = useState('date');
  const [sortDir,        setSortDir]        = useState('desc');

  useEffect(() => {
    getSessions()
      .then(data => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const allEvents  = [...new Set(sessions.map(s => s.event_name).filter(Boolean))].sort();
  const allWeights = [...new Set(sessions.map(s => s.weight_category).filter(Boolean))].sort();

  const filtered = sessions.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      const ok = (s.athlete_blue_name || '').toLowerCase().includes(q) ||
                 (s.athlete_red_name  || '').toLowerCase().includes(q) ||
                 (s.athlete_blue_nationality || '').toLowerCase().includes(q) ||
                 (s.athlete_red_nationality  || '').toLowerCase().includes(q) ||
                 (s.event_name || '').toLowerCase().includes(q);
      if (!ok) return false;
    }
    if (filterEvent  && s.event_name      !== filterEvent)  return false;
    if (filterWeight && s.weight_category !== filterWeight) return false;
    if (filterDateFrom && s.event_date && s.event_date < filterDateFrom) return false;
    if (filterDateTo   && s.event_date && s.event_date > filterDateTo)   return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'event': {
        const va = (a.event_name || '').toLowerCase();
        const vb = (b.event_name || '').toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      case 'score': {
        const va = (Number(a.score_blue) || 0) + (Number(a.score_red) || 0);
        const vb = (Number(b.score_blue) || 0) + (Number(b.score_red) || 0);
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      default: { // date
        const va = a.event_date || a.created_at || '';
        const vb = b.event_date || b.created_at || '';
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
    }
  });

  function clearFilters() {
    setSearch(''); setFilterEvent(''); setFilterWeight('');
    setFilterDateFrom(''); setFilterDateTo('');
  }

  const hasFilters = search || filterEvent || filterWeight || filterDateFrom || filterDateTo;

  const header = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.3 }}>Archivio Incontri</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
        {loading ? '…' : `${filtered.length} di ${sessions.length} incontri`}
      </div>
    </>
  );

  return (
    <PageLayout header={header}>

        {/* Filters */}
        <div className="ds-card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{
                flex: '1 1 240px', background: 'var(--bg-elevated)', border: '1px solid var(--color-border)',
                borderRadius: 6, padding: '8px 12px', color: 'var(--color-text)', fontSize: 12, outline: 'none',
              }}
              placeholder="Cerca atleta, nazione, evento…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 10px', color: 'var(--color-text)', fontSize: 12, outline: 'none' }}
              value={filterEvent}
              onChange={e => setFilterEvent(e.target.value)}
            >
              <option value="">Tutti gli eventi</option>
              {allEvents.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 10px', color: 'var(--color-text)', fontSize: 12, outline: 'none' }}
              value={filterWeight}
              onChange={e => setFilterWeight(e.target.value)}
            >
              <option value="">Tutte le categorie</option>
              {allWeights.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>Da:</span>
              <input type="date"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 8px', color: 'var(--color-text)', fontSize: 11, outline: 'none' }}
                value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>A:</span>
              <input type="date"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 8px', color: 'var(--color-text)', fontSize: 11, outline: 'none' }}
                value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
            </div>
            {hasFilters && (
              <button
                style={{ background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text-dim)', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 11 }}
                onClick={clearFilters}
              >
                Cancella filtri
              </button>
            )}
          </div>

          {/* Sort controls */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>Ordina:</span>
            {[{ key: 'date', label: 'Data' }, { key: 'event', label: 'Evento' }, { key: 'score', label: 'Punteggio' }].map(({ key, label }) => (
              <button key={key}
                style={{
                  background: sortBy === key ? 'var(--accent-bg)' : 'var(--bg-elevated)',
                  border: `1px solid ${sortBy === key ? 'var(--accent)' : 'var(--color-border)'}`,
                  color: sortBy === key ? 'var(--accent)' : 'var(--color-text-dim)',
                  borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                }}
                onClick={() => {
                  if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                  else { setSortBy(key); setSortDir('desc'); }
                }}
              >
                {label}{sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
            ))}
          </div>
        </div>

        {/* Sessions list */}
        {loading ? (
          <div className="ds-card ds-empty-state">Caricamento…</div>
        ) : sorted.length === 0 ? (
          <div className="ds-card ds-empty-state">
            {hasFilters ? 'Nessun incontro corrisponde ai filtri.' : 'Nessun incontro analizzato.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sorted.map(s => {
              const blueWon = s.score_blue != null && s.score_red != null && Number(s.score_blue) > Number(s.score_red);
              const redWon  = s.score_blue != null && s.score_red != null && Number(s.score_red)  > Number(s.score_blue);
              return (
                <div key={s.id} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)', padding: '14px 18px',
                  display: 'flex', alignItems: 'center', gap: 14,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                  onClick={() => navigate(`/report/${s.id}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                >
                  {/* Date badge */}
                  <div style={{ minWidth: 52, textAlign: 'center', padding: '6px 8px', background: 'var(--bg-elevated)', borderRadius: 6 }}>
                    {s.event_date ? (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1.1 }}>
                          {new Date(s.event_date + 'T00:00:00').getDate()}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--color-text-dim)', textTransform: 'uppercase' }}>
                          {new Date(s.event_date + 'T00:00:00').toLocaleDateString('it-IT', { month: 'short', year: '2-digit' })}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>—</div>
                    )}
                  </div>

                  {/* Athletes */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Flag nat={s.athlete_blue_nationality} />
                      <span
                        style={{ color: blueWon ? 'var(--color-blue)' : 'var(--color-text)', cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); navigate(`/atleti/${encodeURIComponent(s.athlete_blue_name)}`); }}
                      >
                        {s.athlete_blue_name}
                      </span>
                      <span style={{ color: 'var(--color-text-dim)', fontSize: 11 }}>vs</span>
                      <Flag nat={s.athlete_red_nationality} />
                      <span
                        style={{ color: redWon ? 'var(--color-red)' : 'var(--color-text)', cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); navigate(`/atleti/${encodeURIComponent(s.athlete_red_name)}`); }}
                      >
                        {s.athlete_red_name}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 3, display: 'flex', gap: 8 }}>
                      {s.event_name && <span>{s.event_name}</span>}
                      {s.weight_category && (
                        <span style={{ background: 'var(--bg-elevated)', borderRadius: 3, padding: '0 5px', fontSize: 10 }}>
                          {s.weight_category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Score */}
                  {s.score_blue != null && (
                    <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>
                      <span style={{ color: blueWon ? 'var(--color-blue)' : 'var(--color-text-dim)' }}>{s.score_blue}</span>
                      <span style={{ color: 'var(--color-text-dim)', margin: '0 4px', fontSize: 12 }}>:</span>
                      <span style={{ color: redWon ? 'var(--color-red)' : 'var(--color-text-dim)' }}>{s.score_red}</span>
                    </div>
                  )}

                  <button
                    style={{ background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}
                    onClick={e => { e.stopPropagation(); navigate(`/report/${s.id}`); }}
                  >
                    Report →
                  </button>
                </div>
              );
            })}
          </div>
        )}
    </PageLayout>
  );
}

export default MatchArchivePage;
